import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Separator } from '@radix-ui/react-dropdown-menu';

import { EditIcon } from '@/assets/icons';
import { Icon, Loader } from '@/components/atoms';
import { TRANSLATION_OVERVIEW_TEST_IDS } from '@/constants/data-testids';
import { LanguageNameMap } from '@/enums/languageCode';
import { TranslationQuestion } from '@/types/management/managementCategories';

export type SetTranslationQuestionOptions = Dispatch<SetStateAction<TranslationQuestion[]>>;

type TranslationOverviewQuestionProps = {
  translationOptions: TranslationQuestion[];
  className?: string;
  isLoading?: boolean;
  onEditClick: (option: TranslationQuestion) => void;
};

export const TranslationOverviewQuestion: FC<TranslationOverviewQuestionProps> = ({
  translationOptions,
  className,
  isLoading,
  onEditClick,
}) => {
  const { t } = useTranslation('components');

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className={className}>
      {translationOptions.map((option, index) => (
        <div key={option.languageCode}>
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name={option.languageCode} width={16} height={16} />
              <p className="grow">{LanguageNameMap[option.languageCode].name}</p>
              <EditIcon
                width={20}
                height={20}
                className="text-custom-warm-grey-400 cursor-pointer"
                onClick={() => onEditClick(option)}
                data-testid={TRANSLATION_OVERVIEW_TEST_IDS.EDIT_TRANSLATION_ICON}
              />
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-body-14-regular text-custom-warm-grey-500 mb-0.5">
                  {t('translationOverviewQuestion.questionNameText')}
                </p>
                <p className="text-body-14-regular text-custom-warm-grey-800"> {option.text}</p>
              </div>
            </div>

            {index !== translationOptions.length - 1 && (
              <Separator className="mt-5 h-[1px] bg-custom-warm-grey-200" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
